import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { iniciales } from '../lib/iniciales';
import logoColor from '../assets/ambec-logo-color.svg';
import { NAV_ITEMS } from './navItems';

/**
 * Shell de administración — guía visual "Talab Edu" adaptada a AMBEC:
 * sidebar tintada de celeste pálido (única superficie con color) con widget de
 * usuario y navegación agrupada; contenido en blanco puro donde cada página
 * renderiza su propio encabezado grande (título bold + subtítulo).
 */

interface AdminNavItem {
  to: string;
  label: string;
  icon: ReactNode;
  /** Marca visual de área aún no funcional. */
  pronto?: boolean;
  /** Página del área de usuario: el link cambia a la vista Usuario y la abre en un clic. */
  vistaUsuario?: boolean;
  /** Nota explicativa (tooltip) del link. */
  ayuda?: string;
}

const elena = NAV_ITEMS.find((i) => i.to === '/agentes/investigador-seia')!;

interface AdminNavGrupo {
  titulo: string;
  items: AdminNavItem[];
}

const iconClass = 'h-4 w-4 shrink-0';
const svgProps = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
};

const ADMIN_GRUPOS: AdminNavGrupo[] = [
  {
    titulo: 'Administración',
    items: [
      {
        to: '/admin/usuarios',
        label: 'Usuarios',
        icon: (
          <svg {...svgProps} className={iconClass}>
            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
        ),
      },
      {
        to: '/admin/conectores',
        label: 'Setup LLMs',
        icon: (
          <svg {...svgProps} className={iconClass}>
            <rect x="4" y="4" width="16" height="16" rx="2" />
            <path d="M9 2v2M15 2v2M9 20v2M15 20v2M2 9h2M2 15h2M20 9h2M20 15h2" />
            <rect x="9" y="9" width="6" height="6" rx="1" />
          </svg>
        ),
      },
      {
        to: '/admin/formato-documentos',
        label: 'Formato Documentos',
        icon: (
          <svg {...svgProps} className={iconClass}>
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <path d="M14 2v6h6M8 13h8M8 17h5" />
          </svg>
        ),
      },
    ],
  },
  {
    titulo: 'Agentes',
    items: [
      {
        to: '/admin/conversaciones-dia',
        label: 'Conversaciones',
        icon: (
          <svg {...svgProps} className={iconClass}>
            <path d="M21 11.5a8.4 8.4 0 0 1-9 8.4 9 9 0 0 1-3.8-.8L3 20l1-4.9a8.4 8.4 0 1 1 17-3.6Z" />
            <path d="M8.5 10.5h7M8.5 13.5h4.5" />
          </svg>
        ),
      },
      {
        to: elena.to,
        label: 'Agente eSEIA',
        icon: elena.icon,
        vistaUsuario: true,
        ayuda: `Abre el chat con Elena. Te cambia a la vista Usuario, porque el chat vive ahí (vuelves con "Cambiar de vista"). ${elena.ayuda ?? ''}`,
      },
    ],
  },
];

function SidebarContenido({ onNavegar }: { onNavegar?: () => void }) {
  const { profile, signOut, esAdmin, reelegirModo, elegirModo } = useAuth();
  const [cerrando, setCerrando] = useState(false);

  async function onCerrarSesion() {
    setCerrando(true);
    try {
      await signOut();
    } finally {
      setCerrando(false);
    }
  }

  return (
    <div className="flex h-full flex-col">
      {/* Marca */}
      <div className="flex items-center gap-2 px-4 pt-6">
        <img src={logoColor} alt="AMBEC" className="h-5 w-auto" />
        <span className="rounded bg-white px-1.5 py-px text-[10px] font-medium text-gris ring-1 ring-inset ring-slate-200">
          Admin
        </span>
      </div>

      {/* Widget de usuario (card blanca sobre la sidebar tintada) */}
      <div className="mx-3 mt-6 flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-2">
        <span
          aria-hidden="true"
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-azul-profundo/10 text-[10px] font-semibold text-azul-profundo"
        >
          {iniciales(profile?.full_name, profile?.email)}
        </span>
        <div className="min-w-0">
          <p className="truncate text-xs font-medium leading-tight text-azul-noche">
            {profile?.full_name || profile?.email}
          </p>
          <p className="truncate text-[10px] font-normal leading-tight text-gris">
            Administrador
          </p>
        </div>
      </div>

      {/* Navegación agrupada */}
      <nav aria-label="Navegación de administración" className="mt-4 flex-1 overflow-y-auto px-3">
        {ADMIN_GRUPOS.map((grupo) => (
          <div key={grupo.titulo} className="mb-4">
            <p className="px-2.5 text-[9px] font-medium uppercase tracking-[0.12em] text-gris/60">
              {grupo.titulo}
            </p>
            <ul className="mt-1.5 flex flex-col gap-0.5">
              {grupo.items.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    title={item.ayuda}
                    onClick={() => {
                      if (item.vistaUsuario) elegirModo('user');
                      onNavegar?.();
                    }}
                    className={({ isActive }) =>
                      [
                        'group relative flex items-center gap-2.5 rounded-lg px-2.5 py-[7px] text-[13px] transition-colors duration-150 ease-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-azul-profundo',
                        isActive
                          ? 'border border-slate-200 bg-white font-medium text-azul-profundo shadow-[0_1px_2px_rgba(1,15,39,0.04)]'
                          : 'border border-transparent font-normal text-texto hover:bg-white/70 hover:text-azul-noche',
                      ].join(' ')
                    }
                  >
                    {({ isActive }) => (
                      <>
                        {isActive && (
                          <span
                            aria-hidden="true"
                            className="absolute left-[3px] top-1/2 h-4 w-[3px] -translate-y-1/2 rounded-full bg-gradient-to-b from-azul-profundo to-celeste"
                          />
                        )}
                        <span
                          aria-hidden="true"
                          className={
                            isActive
                              ? 'text-azul-profundo'
                              : 'text-slate-600 group-hover:text-azul-noche'
                          }
                        >
                          {item.icon}
                        </span>
                        <span className="flex-1">{item.label}</span>
                        {item.pronto && (
                          <span className="rounded bg-white px-1.5 py-px text-[9px] font-medium text-gris ring-1 ring-inset ring-slate-200">
                            Pronto
                          </span>
                        )}
                      </>
                    )}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      {/* Cerrar sesión al pie */}
      <div className="px-3 pb-4">
        {esAdmin && (
          <button
            type="button"
            onClick={reelegirModo}
            title="Estás en modo Administrador. Cambiar a la vista de Usuario (cliente)."
            className="mb-1 flex w-full items-center gap-2.5 rounded-lg px-2.5 py-[7px] text-[13px] font-normal text-texto transition-colors duration-150 ease-out hover:bg-white/70 hover:text-azul-noche focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-azul-profundo"
          >
            <svg viewBox="0 0 24 24" className={`${iconClass} text-azul-profundo`} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M16 3h5v5"/><path d="M8 21H3v-5"/><path d="M21 3 3 21"/></svg>
            Cambiar de vista
          </button>
        )}
        <button
          type="button"
          onClick={onCerrarSesion}
          disabled={cerrando}
          className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-[7px] text-[13px] font-normal text-texto transition-colors duration-150 ease-out hover:bg-white/70 hover:text-azul-noche focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-azul-profundo disabled:cursor-not-allowed disabled:opacity-60"
        >
          <svg
            viewBox="0 0 24 24"
            className={`${iconClass} text-red-500`}
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
            <path d="m16 17 5-5-5-5M21 12H9" />
          </svg>
          {cerrando ? 'Cerrando…' : 'Cerrar sesión'}
        </button>
        <p className="mt-2.5 px-2.5 text-[10px] font-light text-gris/70">
          AMBEC · Administración
        </p>
        <p className="mt-0.5 px-2.5 text-[10px] font-light tabular-nums text-gris/50">
          release {__RELEASE__}
        </p>
      </div>
    </div>
  );
}

export default function AdminLayout() {
  const location = useLocation();
  const [drawerAbierto, setDrawerAbierto] = useState(false);

  useEffect(() => {
    setDrawerAbierto(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!drawerAbierto) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDrawerAbierto(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawerAbierto]);

  return (
    // Lienzo casi blanco de página: se ve en los 4 costados alrededor del marco.
    <div className="app-privada min-h-dvh bg-[#FAFBFC] px-3 py-3 sm:px-4 sm:py-4 lg:px-6 lg:py-5">
      {/* Container global de la app: máx. 1280px centrado (regla de UI) */}
      <div className="mx-auto w-full max-w-[1440px]">
        {/* MARCO de la app: cerrado en los 4 lados, esquinas redondeadas;
            el scroll del contenido ocurre DENTRO del marco. */}
        <div className="flex h-[calc(100dvh-1.5rem)] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_8px_30px_-12px_rgba(1,15,39,0.16)] sm:h-[calc(100dvh-2rem)] lg:h-[calc(100dvh-2.5rem)]">
          {/* Sidebar tintada (desktop) — única superficie con color del shell */}
          <aside className="hidden h-full w-56 shrink-0 border-r border-slate-200/70 bg-[#E8EDF2] lg:block">
            <SidebarContenido />
          </aside>

        {/* Barra superior (solo mobile) */}
        {drawerAbierto && (
          <div className="fixed inset-0 z-40 lg:hidden">
            <div
              className="absolute inset-0 bg-azul-noche/40"
              onClick={() => setDrawerAbierto(false)}
              aria-hidden="true"
            />
            <div
              role="dialog"
              aria-modal="true"
              aria-label="Menú de administración"
              className="absolute inset-y-0 left-0 w-64 border-r border-slate-200 bg-[#E8EDF2] shadow-xl"
            >
              <div className="absolute right-3 top-3">
                <button
                  type="button"
                  onClick={() => setDrawerAbierto(false)}
                  aria-label="Cerrar menú"
                  className="flex h-9 w-9 items-center justify-center rounded-lg text-gris transition-colors duration-150 hover:bg-white hover:text-azul-noche focus-visible:outline focus-visible:outline-2 focus-visible:outline-azul-profundo"
                >
                  <svg
                    viewBox="0 0 24 24"
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                    aria-hidden="true"
                  >
                    <path d="m6 6 12 12M18 6 6 18" />
                  </svg>
                </button>
              </div>
              <SidebarContenido onNavegar={() => setDrawerAbierto(false)} />
            </div>
          </div>
        )}

        {/* Columna principal (scroll interno) */}
        <div className="flex h-full min-w-0 flex-1 flex-col">
          <header className="shrink-0 border-b border-slate-200/70 bg-white lg:hidden">
            <div className="flex h-14 items-center gap-3 px-4">
              <button
                type="button"
                onClick={() => setDrawerAbierto(true)}
                aria-label="Abrir menú"
                aria-expanded={drawerAbierto}
                className="flex h-9 w-9 items-center justify-center rounded-lg text-gris transition-colors duration-150 hover:bg-slate-100 hover:text-azul-noche focus-visible:outline focus-visible:outline-2 focus-visible:outline-azul-profundo"
              >
                <svg
                  viewBox="0 0 24 24"
                  className="h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                  aria-hidden="true"
                >
                  <path d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <img src={logoColor} alt="AMBEC" className="h-6 w-auto" />
            </div>
          </header>

          <main className="relative flex-1 overflow-y-auto px-4 py-6 sm:px-6 lg:px-10 lg:pb-8 lg:pt-10">
            <Outlet />
          </main>
        </div>
        </div>
      </div>
    </div>
  );
}
